import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { Paperclip, X } from "lucide-react";
import api from "../../../utils/axiosInstance";
import FormInput from "../../../components/Form/FormInput";
import FormSelect from "../../../components/Form/FormSelect";
import FormTextarea from "../../../components/Form/TextArea";
import TagMultiSelect from "../../../components/Form/TagMultiSelect";
import { FIELD_ERROR, FIELD_LABEL } from "../../../components/Form/formFieldStyles";
import { getTimezonesWithOffset } from "../../../utils/constants/timezones";
import { getCountries } from "../../../utils/constants/countries";
import { btn } from "../../../components/Button/buttonStyles";

const staticTimezones = getTimezonesWithOffset();
const countries = getCountries();
const vendorTypes = ["Company", "Freelance", "In-House"];
const genders = ["Male", "Female", "Other"];

const MAX_FILES = 5;
const MAX_FILE_MB = 10;
const ACCEPTED_TYPES = [
  "image/png",
  "image/jpeg",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];
const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const INITIAL_FORM = {
  type: "",
  company_name: "",
  country: "",
  language_pair_ids: [],
  specialization_ids: [],
  website: "",
  note: "",
  first_name: "",
  last_name: "",
  email: "",
  timezone: "",
  gender: "",
  phone: "",
  zoom_id: "",
  teams_id: "",
  nationality: "",
};

const REQUIRED = {
  type: "Vendor type is required.",
  country: "Country is required.",
  first_name: "First name is required.",
  last_name: "Last name is required.",
  email: "Email is required.",
  timezone: "Timezone is required.",
};

const formatSize = (bytes) =>
  bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.ceil(bytes / 1024)} KB`;

const FieldNote = ({ children }) => <p className="mt-1 text-[11px] text-gray-500">{children}</p>;

const Shell = ({ agency, children }) => (
  <div className="min-h-screen bg-gray-50/80 px-4 py-10">
    <div className="mx-auto max-w-4xl">
      <div className="mb-6">
        {agency && (
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">{agency}</p>
        )}
        <h1 className="mt-1 text-[22px] font-semibold tracking-tight text-gray-900">
          Vendor Application
        </h1>
      </div>
      {children}
    </div>
  </div>
);

const Message = ({ title, children }) => (
  <div className="rounded-lg border border-gray-100 bg-white p-10 text-center shadow-sm">
    <h2 className="text-base font-semibold text-gray-900">{title}</h2>
    <p className="mt-2 text-sm text-gray-500">{children}</p>
  </div>
);

const VendorApplicationFormPage = () => {
  const { token } = useParams();
  const fileInputRef = useRef(null);
  const captchaRef = useRef(null);

  const [meta, setMeta] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [form, setForm] = useState(INITIAL_FORM);
  const [files, setFiles] = useState([]);
  const [captchaValue, setCaptchaValue] = useState(null);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    api
      .get(`/public/vendor-application/${token}`)
      .then((res) => setMeta(res.data.data))
      .catch((err) =>
        setLoadError(err.response?.data?.message || "This application form could not be loaded.")
      );
  }, [token]);

  const clearError = (name) => {
    setErrors((prev) => ({ ...prev, [name]: "" }));
    setServerError("");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    clearError(name);
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const missing =
      (REQUIRED[name] && !value) || (name === "company_name" && form.type === "Company" && !value);
    setErrors((prev) => ({
      ...prev,
      [name]: missing ? REQUIRED[name] || "Company name is required." : "",
    }));
  };

  const handleFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    let error = "";
    const valid = picked.filter((file) => {
      if (!ACCEPTED_TYPES.includes(file.type)) {
        error = "Allowed file types: PNG, JPG, PDF, DOC, DOCX.";
        return false;
      }
      if (file.size > MAX_FILE_MB * 1024 * 1024) {
        error = `Each file must be ${MAX_FILE_MB} MB or smaller.`;
        return false;
      }
      return true;
    });
    const next = [...files, ...valid];
    if (next.length > MAX_FILES) error = `You can upload at most ${MAX_FILES} files.`;
    setFiles(next.slice(0, MAX_FILES));
    setErrors((prev) => ({ ...prev, files: error }));
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    clearError("files");
  };

  const validate = () => {
    const found = {};
    Object.entries(REQUIRED).forEach(([name, message]) => {
      if (!String(form[name]).trim()) found[name] = message;
    });
    if (form.type === "Company" && !form.company_name.trim())
      found.company_name = "Company name is required.";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      found.email = "Enter a valid email address.";
    if (!form.language_pair_ids.length)
      found.language_pair_ids = "Select at least one language pair.";
    if (SITE_KEY && !captchaValue) found.captcha = "Please verify you are not a robot.";
    setErrors(found);
    return Object.keys(found).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const body = new FormData();
    Object.entries(form).forEach(([key, value]) => {
      if (key === "company_name" && form.type !== "Company") return;
      body.append(key, Array.isArray(value) ? JSON.stringify(value) : value);
    });
    files.forEach((file) => body.append("files", file));
    if (captchaValue) body.append("captchaToken", captchaValue);

    setSubmitting(true);
    try {
      await api.post(`/public/vendor-application/${token}`, body);
      setSubmitted(true);
      window.scrollTo({ top: 0 });
    } catch (err) {
      setServerError(err.response?.data?.message || "Something went wrong while submitting your application.");
      captchaRef.current?.reset();
      setCaptchaValue(null);
    } finally {
      setSubmitting(false);
    }
  };

  if (loadError) {
    return (
      <Shell>
        <Message title="Form Not Available">{loadError}</Message>
      </Shell>
    );
  }

  if (!meta) {
    return (
      <div className="flex min-h-screen justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border border-gray-300 border-t-brand-600" />
      </div>
    );
  }

  if (!meta.enabled) {
    return (
      <Shell agency={meta.agency_name}>
        <Message title="Applications Closed">
          This vendor application form is not accepting applications at the moment.
        </Message>
      </Shell>
    );
  }

  if (submitted) {
    return (
      <Shell agency={meta.agency_name}>
        <Message title="Application Submitted">
          Thank you for applying. Your application has been received and will be reviewed shortly.
        </Message>
      </Shell>
    );
  }

  return (
    <Shell agency={meta.agency_name}>
      <form
        onSubmit={handleSubmit}
        noValidate
        className="bg-white shadow-sm border border-gray-100 rounded-lg p-5 space-y-6"
      >
        <section className="space-y-4">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Vendor Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4 items-start">
            <FormSelect
              label="Vendor Type"
              name="type"
              value={form.type}
              onChange={handleChange}
              onBlur={handleBlur}
              options={vendorTypes}
              error={errors.type}
              required
            />

            {form.type === "Company" ? (
              <FormInput
                label="Company Name"
                name="company_name"
                value={form.company_name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.company_name}
                required
              />
            ) : (
              <div />
            )}

            <FormSelect
              label="Country"
              name="country"
              value={form.country}
              onChange={handleChange}
              onBlur={handleBlur}
              options={countries}
              error={errors.country}
              required
            />

            <div />

            <div>
              <TagMultiSelect
                label="Language Pairs"
                required
                value={form.language_pair_ids}
                onChange={(ids) => {
                  setForm((prev) => ({ ...prev, language_pair_ids: ids }));
                  clearError("language_pair_ids");
                }}
                options={meta.language_pairs.map((lp) => ({ value: lp.id, label: lp.label }))}
                placeholder="Select language pairs"
                error={errors.language_pair_ids}
                emptyMessage="No language pairs available"
              />
              <FieldNote>You can specify additional language pairs in the "Note" field.</FieldNote>
            </div>

            <div>
              <TagMultiSelect
                label="Specializations"
                value={form.specialization_ids}
                onChange={(ids) => setForm((prev) => ({ ...prev, specialization_ids: ids }))}
                options={meta.specializations.map((s) => ({ value: s.id, label: s.name }))}
                placeholder="Select specializations"
                emptyMessage="No specializations available"
              />
              <FieldNote>You can specify additional specializations in the "Note" field.</FieldNote>
            </div>

            <div className="md:col-span-2">
              <span className={FIELD_LABEL}>Files</span>
              <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50/60 p-3">
                {files.length > 0 && (
                  <ul className="mb-3 space-y-1.5">
                    {files.map((file, index) => (
                      <li
                        key={`${file.name}-${index}`}
                        className="flex items-center justify-between gap-3 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-[13px]"
                      >
                        <span className="flex min-w-0 items-center gap-2 text-gray-800">
                          <Paperclip size={13} className="shrink-0 text-gray-400" />
                          <span className="truncate">{file.name}</span>
                          <span className="shrink-0 text-[11px] text-gray-400">{formatSize(file.size)}</span>
                        </span>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="shrink-0 rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                          aria-label={`Remove ${file.name}`}
                        >
                          <X size={14} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".png,.jpg,.jpeg,.pdf,.doc,.docx"
                  onChange={handleFiles}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={files.length >= MAX_FILES}
                  className={btn("secondary", "sm")}
                >
                  Choose Files
                </button>
                <span className="ml-3 text-[11px] text-gray-500">
                  {files.length}/{MAX_FILES} files
                </span>
              </div>
              {errors.files && <p className={FIELD_ERROR}>{errors.files}</p>}
              <FieldNote>
                Price list, CV, certificates, etc. You can upload at most {MAX_FILES} files.
              </FieldNote>
            </div>

            <FormInput
              label="Website"
              name="website"
              value={form.website}
              onChange={handleChange}
            />

            <FormTextarea
              label="Note"
              name="note"
              value={form.note}
              onChange={handleChange}
              rows={1}
            />
          </div>
        </section>

        <section className="space-y-4 pt-2 border-t border-gray-100">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Primary User Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4 items-start">
            <FormInput
              label="First Name"
              name="first_name"
              value={form.first_name}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.first_name}
              required
            />
            <FormInput
              label="Last Name"
              name="last_name"
              value={form.last_name}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.last_name}
              required
            />
            <FormInput
              label="Email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.email}
              required
            />
            <FormSelect
              label="Timezone"
              name="timezone"
              value={form.timezone}
              onChange={handleChange}
              onBlur={handleBlur}
              options={staticTimezones}
              error={errors.timezone}
              required
            />
            <FormSelect
              label="Gender"
              name="gender"
              value={form.gender}
              onChange={handleChange}
              options={genders}
            />
            <FormInput label="Phone" name="phone" value={form.phone} onChange={handleChange} />
            <FormInput label="Zoom ID" name="zoom_id" value={form.zoom_id} onChange={handleChange} />
            <FormInput label="Teams ID" name="teams_id" value={form.teams_id} onChange={handleChange} />
            <FormInput
              label="Nationality"
              name="nationality"
              value={form.nationality}
              onChange={handleChange}
            />
          </div>
        </section>

        {serverError && (
          <div className="bg-danger-50 border border-danger-200 text-danger-700 text-sm px-3 py-2 rounded">
            {serverError}
          </div>
        )}

        <div className="pt-3 border-t border-gray-100 space-y-3">
          <p className="text-xs text-gray-500">
            Fields marked with <span className="text-danger-500">*</span> are mandatory.
          </p>
          {SITE_KEY && (
            <div>
              <ReCAPTCHA
                ref={captchaRef}
                sitekey={SITE_KEY}
                onChange={(value) => {
                  setCaptchaValue(value);
                  clearError("captcha");
                }}
              />
              {errors.captcha && <p className={FIELD_ERROR}>{errors.captcha}</p>}
            </div>
          )}
          <div className="flex gap-2 text-sm">
            <button type="submit" disabled={submitting} className={btn("primary", "sm")}>
              {submitting ? "Submitting..." : "Submit"}
            </button>
          </div>
        </div>
      </form>
    </Shell>
  );
};

export default VendorApplicationFormPage;
